import { Component, ElementRef, ViewChild, OnDestroy,
	AfterViewInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import * as THREE from 'three';
import * as _ from 'underscore';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { SiteContentItem } from '../app.component';
import { TranslationService } from '../app.translation-service';

const CUBE_COLOR = 0xc4ab62;
const CUBE_INSIDE_COLOR = 0x33271d;
const CUBE_COLOR_HIGHLIGHT = 0xe0c575;
const RESOLUTION = 720;
const CAMERA_DISTANCE = 4.5;
const MAX_CAMERA_ANGLE_V = Math.PI * 0.25;
const MIN_CAMERA_ANGLE_V = -Math.PI * 0.1;
const CAMERA_DRAG_SPEED = 0.0001;
const CAMERA_FOCUS_SPEED = 900;
const CUBE_OPEN_SPEED = 0.003;
const CUBE_FOCUS_DISTANCE = 2.5;

/**
 * CubeState
 */
enum CubeState {
	Interactive = 0,
	Opening = 1,
	Closing = 2,
	Opened = 3
}

/**
 * AnimationProperties
 */
interface AnimationProperties {
	cubeIndex: number | null;
	cube: any;
	cameraInitialAngle: THREE.Vector2;
	cameraFinalAngle: THREE.Vector2;
	cubeFinalPosition: THREE.Vector3;
	cubeInitialPosition: THREE.Vector3;
	sequence: number;
	animationDuration: number;
	time: number;
	percentage: number;
}

@Component({
	selector: 'app-cube',
	templateUrl: './cube.component.html',
	styleUrls: ['./cube.component.less'],
})
export class CubeComponent implements AfterViewInit, OnChanges, OnDestroy {

	@ViewChild('canvas')
	private canvasRef: ElementRef | undefined;

	private renderer: any;
	private camera: any;
	private scene: any;
	private cube: any;

	public state: CubeState;

	private isMouseDown: boolean;
	private mouse: THREE.Vector2;
	private lastMouseDownCoordinates: THREE.Vector2;
	private openCubeStatus?: AnimationProperties;
	private cameraAngle: THREE.Vector2;
	private cameraInitialAngles: THREE.Vector2;
	private cameraDragSpeed: THREE.Vector2;
	private cameraDistance: number;
	private cameraInitialDistance: number;
	private resetCameraDragSpeed: any;
	private lastTime: number;
	private animationFrame: any;
	private portraitMode: boolean;

	private raycaster: THREE.Raycaster;
	private intersectedObject: any;

	private cubes: any[];
	private selectedCube?: number | null;

	/**
	 * Selected cube
	 */
	@Input()
	public selectedCubeInfo?: {selected?: number | null} | null;

	/**
	 * Highlighted cube
	 */
	 @Input()
	public highlightedCube?: number | null;

	/**
	 * Site content
	 */
	 @Input()
	public siteContent?: SiteContentItem[] | null;

	/**
	 * Selected cube change
	 */
	 @Output()
 	public selectedCubeChange: EventEmitter<number | null> = new EventEmitter<number | null>();

	/**
	 * Constructor
	 */
	constructor(public i18n: TranslationService) {
		this.mouse = new THREE.Vector2(0, 0);
		this.lastMouseDownCoordinates = new THREE.Vector2(0, 0);
		this.raycaster = new THREE.Raycaster();
		this.cubes = [];
		this.cameraAngle = new THREE.Vector2(0, 0);
		this.cameraInitialAngles = new THREE.Vector2(0, 0);
		this.cameraInitialAngles = new THREE.Vector2(0, 0);
		this.cameraDragSpeed = new THREE.Vector2(0, 0);
		this.isMouseDown = false;
		this.lastTime = 0;
		this.state = CubeState.Interactive;
		this.cameraDistance = CAMERA_DISTANCE;
		this.cameraInitialDistance = CAMERA_DISTANCE;
		this.portraitMode = true;
		this.openCubeStatus = {
			cubeIndex: null,
			cube: null,
			cameraFinalAngle: new THREE.Vector2(0, 0),
			cameraInitialAngle: new THREE.Vector2(0, 0),
			cubeFinalPosition: new THREE.Vector3(0, 0, 0),
			cubeInitialPosition: new THREE.Vector3(0, 0, 0),
			percentage: 0,
			sequence: 0,
			animationDuration: 0,
			time: 0
		};

		this.resetCameraDragSpeed = _.debounce(() => {
			this.cameraDragSpeed.x = CAMERA_DRAG_SPEED;
			this.cameraDragSpeed.y = 0;
		}, 1000);
	}

	//#region private methods

	/**
	 * Calculate intersected object
	 */
	private calculateIntersectedObject(): void {
		// find intersections for sub-cubes
		this.raycaster.setFromCamera(this.mouse, this.camera);
		const intersects = this.raycaster.intersectObjects( this.cube.children );

		if ( intersects.length > 0 && !(this.intersectedObject instanceof THREE.Sprite))
		{
			this.intersectedObject = intersects[0].object;
			this.intersectedObject.material[0].color.setHex(CUBE_COLOR_HIGHLIGHT);
		} else {
			this.intersectedObject = null;
		}
	}

	/**
	 * Update
	 */
	private update = () => {
		this.animationFrame = requestAnimationFrame(this.update);

		const now = Date.now();
		const delta = Date.now() - this.lastTime;
		this.lastTime = now;

		if (this.highlightedCube != null && this.highlightedCube >= 0 && this.highlightedCube < this.cubes.length) {
			this.intersectedObject = this.cubes[this.highlightedCube];
		}

		for (const cube of this.cubes) {
			if (cube.label != null) {
				if (cube === this.intersectedObject && this.state === CubeState.Interactive) {
					cube.label.visible = true;
					cube.material[0].color.setHex(CUBE_COLOR_HIGHLIGHT);
				} else {
					cube.label.visible = false;
					cube.material[0].color.setHex(CUBE_COLOR);
				}
			}
		}

		// STATE = Interactive
		if (this.state === CubeState.Interactive) {
			// drag camera angle automatically
			this.cameraAngle.x += this.cameraDragSpeed.x * delta;
			this.cameraAngle.y += this.cameraDragSpeed.y * delta;

			this.calculateIntersectedObject();

		// STATE = Opening
		} else if (this.state === CubeState.Opening) {
			if (this.openCubeStatus != null) {
				this.openCubeStatus.time += delta;

				if (this.openCubeStatus.time >= this.openCubeStatus.animationDuration) {
					this.openCubeStatus.time = this.openCubeStatus.animationDuration;
					this.state = CubeState.Opened;
				}

				const x = this.openCubeStatus.time / this.openCubeStatus.animationDuration;
				this.openCubeStatus.percentage = 1 - Math.pow(x - 1, 2);

				this.cameraAngle = new THREE.Vector2(
					this.openCubeStatus.cameraInitialAngle.x +
					((this.openCubeStatus.cameraFinalAngle.x - this.openCubeStatus.cameraInitialAngle.x)
					* this.openCubeStatus.percentage),
					this.openCubeStatus.cameraInitialAngle.y +
					((this.openCubeStatus.cameraFinalAngle.y - this.openCubeStatus.cameraInitialAngle.y)
					* this.openCubeStatus.percentage)
				);

				this.cameraDistance = this.cameraInitialDistance - (CUBE_FOCUS_DISTANCE * this.openCubeStatus.percentage);

				const cubePosition = this.openCubeStatus.cubeInitialPosition.clone();
				cubePosition.lerp(this.openCubeStatus.cubeFinalPosition, this.openCubeStatus.percentage);
				this.openCubeStatus?.cube?.position.copy(cubePosition);
			}
		// STATE = Closing
		} else if (this.state === CubeState.Closing) {
			if (this.openCubeStatus != null) {
				this.openCubeStatus.time += delta;

				if (this.openCubeStatus.time >= this.openCubeStatus.animationDuration) {
					this.openCubeStatus.time = this.openCubeStatus.animationDuration;
					this.state = CubeState.Interactive;

					this.cameraDragSpeed.x = CAMERA_DRAG_SPEED;
					this.cameraDragSpeed.y = 0;
				}

				const x = this.openCubeStatus.time / this.openCubeStatus.animationDuration;
				this.openCubeStatus.percentage = 1 - Math.pow(x - 1, 2);

				this.cameraAngle = new THREE.Vector2(
					this.openCubeStatus.cameraInitialAngle.x +
					((this.openCubeStatus.cameraFinalAngle.x - this.openCubeStatus.cameraInitialAngle.x)
					* this.openCubeStatus.percentage),
					this.openCubeStatus.cameraInitialAngle.y +
					((this.openCubeStatus.cameraFinalAngle.y - this.openCubeStatus.cameraInitialAngle.y)
					* this.openCubeStatus.percentage)
				);

				this.cameraDistance = this.cameraInitialDistance - (CUBE_FOCUS_DISTANCE * ( 1 - this.openCubeStatus.percentage));

				const cubePosition = this.openCubeStatus.cubeInitialPosition.clone();
				cubePosition.lerp(this.openCubeStatus.cubeFinalPosition, this.openCubeStatus.percentage);
				if (this.openCubeStatus.cube != null) {
					this.openCubeStatus.cube.position.copy(cubePosition);
				}

				if (this.state === CubeState.Interactive) {
					this.openCubeStatus.cube = null;
					this.openCubeStatus.cubeIndex = null;
				}
			}
		}

		// limit camera vertical angle
		if (this.cameraAngle.y > MAX_CAMERA_ANGLE_V) {
			this.cameraAngle.y = MAX_CAMERA_ANGLE_V;
		} else if (this.cameraAngle.y < MIN_CAMERA_ANGLE_V) {
			this.cameraAngle.y = MIN_CAMERA_ANGLE_V;
		}
		this.cameraAngle.x = this.cameraAngle.x % (Math.PI * 2);
		if (this.cameraAngle.x < 0) {
			this.cameraAngle.x += Math.PI * 2;
		}

		// update camera position
		const a = this.cameraDistance * Math.cos(this.cameraAngle.y);
		this.camera.position.set(
			a * Math.cos(this.cameraAngle.x),
			this.cameraDistance * Math.sin(this.cameraAngle.y),
			a * Math.sin(this.cameraAngle.x)
			);
		this.camera.lookAt(new THREE.Vector3(0, 0, 0));

		this.renderer.render(this.scene, this.camera);

		this.openSelectedSubCube();
	}

	/**
	 * On resize
	 */
	private onResize = (): void => {
		const width = window.innerWidth;
		const height = window.innerHeight;

		// modify perspective camera
		this.camera.aspect = width / height;
		this.camera.updateProjectionMatrix();

		if (height > width) {
			this.portraitMode = true;
			this.cameraInitialDistance = CAMERA_DISTANCE * 1.5;
			this.cameraDistance = this.cameraInitialDistance;
		} else {
			this.portraitMode = false;
			this.cameraInitialDistance = CAMERA_DISTANCE;
			this.cameraDistance = this.cameraInitialDistance;
		}

		// set new renderer canvas size
		this.renderer.setSize(RESOLUTION * this.camera.aspect, RESOLUTION);
		if (this.canvasRef) {
			this.canvasRef.nativeElement.style.width = width + 'px';
			this.canvasRef.nativeElement.style.height = height + 'px';
		}
	}

	/**
	 * Load assets
	 */
	private async loadAssets(): Promise<any> {
		// load textures
		const specularTexture = await new THREE.TextureLoader().loadAsync('assets/cube/textures/specular.png');
		specularTexture.mapping = THREE.EquirectangularReflectionMapping;

		const shadowTexture = await new THREE.TextureLoader().loadAsync('assets/cube/textures/shadow.png');

		// load meshes
		const loader = new OBJLoader();
		const cubeBittenMesh: THREE.Mesh = ((await loader.loadAsync('assets/cube/models/cube_bite.obj')) as any).children[0];
		const cubeNormalMesh: THREE.Mesh = ((await loader.loadAsync('assets/cube/models/cube_norm.obj')) as any).children[0];

		return {
			cubeBittenMesh,
			cubeNormalMesh,
			specularTexture,
			shadowTexture,
		};
	}

	/**
	 * Create materials
	 */
	private createMaterials(specularTexture: any): THREE.Material[] {
		const materials = [
			new THREE.MeshBasicMaterial({color: CUBE_COLOR, envMap: specularTexture, reflectivity: 0.8}),
			new THREE.MeshBasicMaterial({color: CUBE_INSIDE_COLOR}),
			new THREE.RawShaderMaterial( {
				uniforms: {
					color: { type: 'c', value: { r: 0.3, g: 0.23, b: 0.16 } } as any,
					shininess: { type: 'f', value: 125.0} as any,
				},
				vertexShader: `
					precision highp float;

					attribute vec3 position;
					attribute vec3 normal;
					attribute vec2 uv;

					uniform mat4 modelViewMatrix;
					uniform mat4 projectionMatrix;

					varying vec3 fNormal;
					varying vec3 fPosition;
					varying vec3 fWorldPosition;

					void main()
					{
						fNormal = normal;

						fWorldPosition = position;
						vec4 pos = modelViewMatrix * vec4(position, 1.0);
						fPosition = pos.xyz;

						gl_Position = projectionMatrix * pos;
					}
				`,
				fragmentShader: `
					precision highp float;

					uniform mat3 normalMatrix;
					uniform mat4 modelViewMatrix;
					uniform vec3 color;
					uniform float shininess;

					varying vec3 fPosition;
					varying vec3 fWorldPosition;
					varying vec3 fNormal;

					void main()
					{
						vec3 normal = normalize(normalMatrix * fNormal);

						float factor = max(0.0, dot(normal, vec3(0.0, 0.0, 1.0)));

						vec4 colorSpecular = vec4(0.95, 0.85, 0.8, 1.0) * pow(factor, shininess) * 0.3;
						vec4 colorBackLight = vec4(color.x * 0.4, color.y * 0.4, color.z * 0.4, 1.0);
						vec4 colorMain = vec4(color, 1.0) * pow(factor, 7.0);

						gl_FragColor = colorMain + colorSpecular + colorBackLight;
					}
				`
			}),
		];
		return materials;
	}

	/**
	 * Make text sprite
	 */
	 private makeTextSprite(id: number, cube: any): any {
		const fontface = 'Arial';
		const fontsize = 36;
		const borderThickness = 20;
		const textColor = { r: 255, g: 255, b: 255, a: 1.0 };
		const backgroundColor = { r: 0, g: 0, b: 0, a: 1.0 };

		const message = this.siteContent != null ? this.siteContent.find(c => c.id === id)?.name : '';

		const canvas = document.createElement('canvas');
		const context: CanvasRenderingContext2D = canvas.getContext('2d') as CanvasRenderingContext2D;
		context.font = 'Bold ' + fontsize + 'px ' + fontface;

		// get size data (height depends only on font size)
		const metrics = context.measureText( message as string );
		const textWidth = metrics.width;

		// background color
		context.fillStyle   = 'rgba(' + backgroundColor.r + ',' + backgroundColor.g + ','
									+ backgroundColor.b + ',' + backgroundColor.a + ')';

		this.roundRect(context, borderThickness / 2, borderThickness / 2, textWidth + borderThickness, fontsize * 1.4 + borderThickness, 6);

		// text color
		context.fillStyle = 'rgba(' + textColor.r + ',' + textColor.g + ','
		+ textColor.b + ',' + textColor.a + ')';

		context.fillText( message as string, borderThickness, fontsize + borderThickness);

		// canvas contents will be used for a texture
		const texture = new THREE.Texture(canvas);
		texture.needsUpdate = true;

		const spriteMaterial = new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true, opacity: 1});
		const sprite = new THREE.Sprite( spriteMaterial );
		sprite.scale.set(1.0, 0.5, 1.0);

		sprite.center.set(0.25, 0.5);

		sprite.position.copy(cube.position);
		sprite.position.multiplyScalar(1.5);
		sprite.visible = false;
		(cube as any).label = sprite;

		// const material = new THREE.LineBasicMaterial({
		// 	color: 0x000000
		// });

		// const points = [];
		// points.push( cube.position.clone().multiplyScalar(2) );
		// points.push( sprite.position.clone() );

		// points[0].add(new THREE.Vector3(0, 0.1, 0));
		// points[1].add(new THREE.Vector3(0, 0.1, 0));

		// const geometry = new THREE.BufferGeometry().setFromPoints( points );

		// const line = new THREE.Line( geometry, material );

		// this.scene.add( line );
		this.scene.add(sprite);

		return sprite;
	}

	/**
	 * Round rect
	 */
	private roundRect(ctx: any, x: number, y: number, w: number, h: number, r: number): void{
		ctx.beginPath();
		ctx.moveTo(x + r, y);
		ctx.lineTo(x + w - r, y);
		ctx.quadraticCurveTo(x + w, y, x + w, y + r);
		ctx.lineTo(x + w, y + h - r);
		ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
		ctx.lineTo(x + r, y + h);
		ctx.quadraticCurveTo(x, y + h, x, y + h - r);
		ctx.lineTo(x, y + r);
		ctx.quadraticCurveTo(x, y, x + r, y);
		ctx.closePath();
		ctx.fill();
		ctx.stroke();
	}

	/**
	 * Create main cube
	 */
	private createCube(assets: any): void {
		if (!this.siteContent) {
			return;
		}
		// create cube Materials
		const cubeBiteMeshMaterials = this.createMaterials(assets.specularTexture);
		const cubeNormMeshMaterials = [cubeBiteMeshMaterials[0].clone(), cubeBiteMeshMaterials[1].clone()];

		// build cube
		const group = new THREE.Group();

		const offset = 1.05;

		// cube 01
		const cube01 = new THREE.Mesh(assets.cubeBittenMesh.geometry, cubeBiteMeshMaterials);
		// position
		cube01.position.x = 0.5;
		cube01.position.y = 0.5;
		cube01.position.z = 0.5;
		group.add(cube01);
		this.cubes.push(cube01);
		// label
		this.makeTextSprite(0, cube01);

		const cube02 = new THREE.Mesh(assets.cubeNormalMesh.geometry, cubeNormMeshMaterials);
		// position
		cube02.position.x = -0.5 * offset;
		cube02.position.y = 0.5;
		cube02.position.z = 0.5;
		group.add(cube02);
		this.cubes.push(cube02);
		// label
		this.makeTextSprite(1, cube02);

		const cube03 = new THREE.Mesh(assets.cubeNormalMesh.geometry, [cubeBiteMeshMaterials[0].clone(), cubeBiteMeshMaterials[1].clone()]);
		// position
		cube03.position.x = -0.5 * offset;
		cube03.position.y = 0.5;
		cube03.position.z = -0.5 * offset;
		// rotation
		cube03.rotation.y = -Math.PI * 0.5;
		group.add(cube03);
		this.cubes.push(cube03);
		// label
		this.makeTextSprite(2, cube03);

		const cube04 = new THREE.Mesh(assets.cubeNormalMesh.geometry, [cubeBiteMeshMaterials[0].clone(), cubeBiteMeshMaterials[1].clone()]);
		// position
		cube04.position.x = 0.5;
		cube04.position.y = 0.5;
		cube04.position.z = -0.5 * offset;
		// rotation
		cube04.rotation.y = Math.PI;
		group.add(cube04);
		this.cubes.push(cube04);
		// label
		this.makeTextSprite(3, cube04);

		const cube05 = new THREE.Mesh(assets.cubeNormalMesh.geometry, [cubeBiteMeshMaterials[0].clone(), cubeBiteMeshMaterials[1].clone()]);
		// position
		cube05.position.x = 0.5;
		cube05.position.y = -0.5 * offset;
		cube05.position.z = 0.5;
		// rotation
		cube05.rotation.y = Math.PI * 0.5;
		cube05.rotation.z = Math.PI * 0.5;
		group.add(cube05);
		this.cubes.push(cube05);
		// label
		this.makeTextSprite(4, cube05);

		const cube06 = new THREE.Mesh(assets.cubeNormalMesh.geometry, [cubeBiteMeshMaterials[0].clone(), cubeBiteMeshMaterials[1].clone()]);
		// position
		cube06.position.x = -0.5 * offset;
		cube06.position.y = -0.5 * offset;
		cube06.position.z = 0.5;
		// rotation
		cube06.rotation.z = Math.PI * 0.5;
		group.add(cube06);
		this.cubes.push(cube06);
		// label
		this.makeTextSprite(5, cube06);

		const cube07 = new THREE.Mesh(assets.cubeNormalMesh.geometry, [cubeBiteMeshMaterials[0].clone(), cubeBiteMeshMaterials[1].clone()]);
		// position
		cube07.position.x = -0.5 * offset;
		cube07.position.y = -0.5 * offset;
		cube07.position.z = -0.5 * offset;
		// rotation
		cube07.rotation.x = -Math.PI * 0.5;
		cube07.rotation.y = -Math.PI * 0.5;
		group.add(cube07);
		this.cubes.push(cube07);
		// label
		this.makeTextSprite(6, cube07);

		const cube08 = new THREE.Mesh(assets.cubeNormalMesh.geometry, [cubeBiteMeshMaterials[0].clone(), cubeBiteMeshMaterials[1].clone()]);
		// position
		cube08.position.x = 0.5;
		cube08.position.y = -0.5 * offset;
		cube08.position.z = -0.5 * offset;
		// rotation
		cube08.rotation.x = -Math.PI;
		cube08.rotation.y = Math.PI * 0.5;
		group.add(cube08);
		this.cubes.push(cube08);
		// label
		this.makeTextSprite(7, cube08);

		// group.position.x = 0.5;
		group.position.y = 0.15;
		// group.position.z = 0.5;
		this.cube = group;

		this.scene.add(this.cube);

		// create cube shadow
		const shadow = new THREE.Mesh(new THREE.PlaneGeometry(2.85, 2.85), new THREE.MeshBasicMaterial({
			map: assets.shadowTexture, transparent: true, color: 0x000000, opacity: 0.75}));
		shadow.rotation.x = -Math.PI * 0.5;
		shadow.position.y = -1;
		this.scene.add(shadow);
	}

	/**
	 * Calculate camera final angle
	 */
	private calculateCameraFinalAngle(cameraInitialAngle: THREE.Vector2, cameraFinalAngle: THREE.Vector2): void {
		if (Math.abs(cameraFinalAngle.x - cameraInitialAngle.x) > Math.PI) {
			cameraFinalAngle.x = cameraInitialAngle.x + (((Math.PI * 2) - Math.abs(cameraFinalAngle.x - cameraInitialAngle.x))
			* -Math.sign(cameraFinalAngle.x - cameraInitialAngle.x));
		}
	}

	/**
	 * Open selected sub-cube
	 */
	private openSelectedSubCube(): void {
		if (this.openCubeStatus != null && this.selectedCube !== this.openCubeStatus.cubeIndex) {
			if (this.state === CubeState.Opened) {
				this.state = CubeState.Closing;

				const cameraInitialAngle = this.cameraAngle.clone();
				const cameraFinalAngle = new THREE.Vector2(Math.PI * 0.25, Math.PI * 0.15);

				this.calculateCameraFinalAngle(cameraInitialAngle, cameraFinalAngle);

				this.openCubeStatus = {
					cubeIndex: this.openCubeStatus.cubeIndex,
					cube: this.openCubeStatus.cube,
					cameraInitialAngle,
					cameraFinalAngle,
					cubeFinalPosition: this.openCubeStatus.cubeInitialPosition.clone(),
					cubeInitialPosition: this.openCubeStatus.cube.position,
					sequence: 0,
					percentage: 0,
					time: 0,
					animationDuration: CAMERA_FOCUS_SPEED
				};
			} else if (this.state === CubeState.Interactive && this.selectedCube != null) {
				this.state = CubeState.Opening;

				const subCube = this.cubes[this.selectedCube];

				const cameraInitialAngle = this.cameraAngle.clone();
				const cameraFinalAngle = new THREE.Vector2(
					((this.selectedCube % 4) * Math.PI * 0.5) + (Math.PI * 0.25),
					this.selectedCube <= 3 ? Math.PI * 0.25 : -Math.PI * 0.25
				);

				this.calculateCameraFinalAngle(cameraInitialAngle, cameraFinalAngle);

				this.openCubeStatus = {
					cubeIndex: this.selectedCube,
					cube: this.cubes[this.selectedCube],
					cameraInitialAngle,
					cameraFinalAngle,
					cubeFinalPosition: subCube.position.clone().add(new THREE.Vector3(0, this.selectedCube <= 3 ? 3 : -3, 0)),
					cubeInitialPosition: subCube.position.clone(),
					sequence: 0,
					percentage: 0,
					time: 0,
					animationDuration: CAMERA_FOCUS_SPEED
				};
			}
		}
	}

	/**
	 * Select highlighted sub-cube
	 */
	private selectedHighlightedSubCube(): void {
		this.selectedCube = this.cubes.findIndex(c => c === this.intersectedObject);
		if (this.selectedCube < 0 || this.selectedCube >= this.cubes.length) {
			this.selectedCube = null;
		}
		this.selectedCubeChange.emit(this.selectedCube);
	}

	//#endregion

	//#region public methods

	/**
	 * After view init
	 */
	public async ngAfterViewInit(): Promise<void> {
		this.renderer = new THREE.WebGLRenderer({ canvas: this.canvasRef?.nativeElement, alpha: true, preserveDrawingBuffer: true,
			antialias: true });
		this.renderer.sortObjects = false;
		this.renderer.setClearColor(0x000000, 0);

		this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 1000);
		this.cameraAngle = new THREE.Vector2(Math.PI * 0.25, Math.PI * 0.15);
		this.cameraDragSpeed.x = CAMERA_DRAG_SPEED;

		this.scene = new THREE.Scene();

		const sunlight = new THREE.DirectionalLight(0xFFFFFF, 1);
		sunlight.position.x = 2;
		sunlight.position.y = 3;
		sunlight.position.z = 1;
		this.scene.add(sunlight);

		const ambientLight = new THREE.AmbientLight(0xFFFFFF, 0.2);
		this.scene.add(ambientLight);

		// const axesHelper = new THREE.AxesHelper( 3 );
		// this.scene.add( axesHelper );

		this.raycaster = new THREE.Raycaster();

		// load assets
		const assets = await this.loadAssets();

		// create cube
		this.createCube(assets);

		// subscribe to resize event
		window.addEventListener('resize', this.onResize as any);
		// resize canvas
		this.onResize();
		// start animation loop
		this.update();
	}

	/**
	 * On changes
	 */
	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.selectedCubeInfo) {
			this.selectedCube = this.selectedCubeInfo?.selected;
		}
	}

	/**
	 * On destroy
	 */
	public ngOnDestroy(): void {
		window.removeEventListener('resize', this.onResize as any);
		cancelAnimationFrame(this.animationFrame);
	}

	//#endregion

	//#region events

	/**
	 * Get coordinates from event
	 */
	private getCoordsFromEvent(event: MouseEvent | TouchEvent): {x: number, y: number} {
		if (event instanceof MouseEvent) {
			return {
				x: event.clientX,
				y: event.clientY
			};
		} else if (event instanceof TouchEvent) {
			return {
				x: event.touches != null && event.touches.length > 0 ? event.touches[0].clientX : 0,
				y: event.touches != null && event.touches.length > 0 ? event.touches[0].clientY : 0,
			};
		}

		return {x: 0, y: 0};
	}

	/**
	 * Mouse move
	 */
	public mouseMove(event: MouseEvent | TouchEvent): void {
		if (this.state === CubeState.Interactive) {
			const coordinates = this.getCoordsFromEvent(event);

			this.mouse.x = ( coordinates.x / window.innerWidth ) * 2 - 1;
			this.mouse.y = - ( coordinates.y / window.innerHeight ) * 2 + 1;

			if (this.isMouseDown) {
				const mouseDragX = coordinates.x - this.lastMouseDownCoordinates.x;
				let mouseDragY = coordinates.y - this.lastMouseDownCoordinates.y;

				if (window.innerHeight >= window.innerWidth * 1.5) {
					mouseDragY = 0;
				}

				this.cameraAngle.x = this.cameraInitialAngles.x + (mouseDragX * 0.005);
				this.cameraAngle.y = this.cameraInitialAngles.y + (mouseDragY * 0.005);

				if (this.cameraAngle.y < 0) {
					this.cameraAngle.y = 0;
				}
			}
		}
	}

	/**
	 * Mouse down
	 */
	public mouseDown(event: MouseEvent | TouchEvent): void {
		this.isMouseDown = true;

		if (this.state === CubeState.Interactive) {
			const coordinates = this.getCoordsFromEvent(event);

			this.lastMouseDownCoordinates.x = coordinates.x;
			this.lastMouseDownCoordinates.y = coordinates.y;

			this.cameraInitialAngles.x = this.cameraAngle.x;
			this.cameraInitialAngles.y = this.cameraAngle.y;

			this.cameraDragSpeed.x = 0;
			this.cameraDragSpeed.y = 0;
		}
	}

	/**
	 * Mouse up
	 */
	public mouseUp(event: MouseEvent | TouchEvent): void {
		this.isMouseDown = false;

		if (this.state === CubeState.Interactive) {
			const coordinates = this.getCoordsFromEvent(event);

			if (Math.abs(coordinates.x - this.lastMouseDownCoordinates.x) < 3 && Math.abs(coordinates.y - this.lastMouseDownCoordinates.y) < 3) {
				this.mouse.x = ( coordinates.x / window.innerWidth ) * 2 - 1;
				this.mouse.y = - ( coordinates.y / window.innerHeight ) * 2 + 1;
				this.calculateIntersectedObject();

				this.selectedHighlightedSubCube();
			}

			if (this.selectedCube == null) {
				this.resetCameraDragSpeed();
			}
		}
	}

	//#endregion
}
